import React, { useState, useEffect } from "react";
import LayoutAdmin from "../../components/layout/LayoutAdmin";
import UserInfoModal from "../../components/models/UserInfoModal";
import apiService from "../../actions/apiService";

const AdminUsers = () => {
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(100); // as per curl (capped by backend at 100)
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      // backend mounted at /api, apiService likely prefixes it; using /admin/users
      const res = await apiService.get("/admin/users", {
        page,
        limit,
        search: search || undefined,
      });
      if (res?.success) {
        const list = res?.data?.users || res?.data?.data?.users || [];
        setUsers(Array.isArray(list) ? list : []);
      } else {
        setError(res?.error || "Failed to load users");
        setUsers([]);
      }
    } catch (_) {
      setError("Network error. Please try again.");
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, search]);

  const handleOpenUser = (u) => {
    setSelectedUser(u); // Pass full user object
    setUserModalOpen(true);
  };

  const handleBlockUser = async (userId) => {
    try {
      const res = await apiService.post(`/admin/users/${userId}/block`, {
        reason: "Blocked by admin",
      });
      if (res?.success) {
        // Refresh user list
        fetchUsers();
      } else {
        alert(res?.error || "Failed to block user");
      }
    } catch (err) {
      alert("Network error. Please try again.");
    }
  };

  const handleUnblockUser = async (userId) => {
    try {
      const res = await apiService.post(`/admin/users/${userId}/unblock`);
      if (res?.success) {
        // Refresh user list
        fetchUsers();
      } else {
        alert(res?.error || "Failed to unblock user");
      }
    } catch (err) {
      alert("Network error. Please try again.");
    }
  };

  return (
    <LayoutAdmin>
      {/* Header */}
      <div className="absolute lg:hidden top-0 left-0 h-72 w-full bg-blue-gradient"></div>
      <div className="relative mb-8 z-[10]">
        <div className="flex px-5 items-center justify-between">
          <div>
            <h2 className="text-page-title md:text-page-title-d text-white lg:text-[#1E1E1E] font-rubik">
              User managment
            </h2>
            <p className="text-small-price opacity-50 mt-4 md:text-small-price text-white lg:text-[#1E1E1E] !font-normal">
              Promote, demote, or block users
            </p>
          </div>
        </div>
      </div>

      {/* Search users */}
      <div className="px-5 lg:px-0 lg:ps-5 relative">
        <div className="bg-white border border-[#DCEDFF] rounded-[12px] p-5 mb-5">
          <h4 className="font-Rubik text-[24px] font-[500] text-esdarkGray mb-2">
            Search users
          </h4>
          <p className="text-[16px] opacity-50 font-[400] text-[#000025] mb-8">
            Search users by name or email address.
          </p>
          <div className="flex flex-col sm:flex-row items-center gap-2">
            <input
              type="text"
              placeholder="Search users..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="flex-1 w-full border border-[#00000026] rounded-[8px] px-3 py-2 text-[14px] placeholder:text-[#1E1E1E] placeholder:opacity-50 placeholder:font-[400] outline-none"
            />
            <button
              onClick={() => {
                setPage(1);
                setSearch(searchInput.trim());
              }}
              className="inline-flex justify-center items-center gap-2 bg-esblue text-white text-[14px] min-w-full sm:min-w-[174px] px-4 py-2 rounded-[8px]"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
              </svg>
              Search
            </button>
          </div>
          {error && <div className="mt-4 text-sm text-red-600">{error}</div>}
        </div>
      </div>

      {/* Table (desktop) */}
      <div className="px-5 lg:px-0 lg:ps-5">
        <div className="w-full overflow-x-auto bg-white border border-[#DCEDFF] rounded-[12px]">
          <table className="w-[97%] ms-5 mr-5 mx-auto mt-5 mb-2 min-w-[900px]">
            <thead className="bg-[#1E1E1E0D] text-subsection-title-d text-[#1E1E1E]">
              <tr>
                <th className="text-left min-w-[150px] py-3 px-5 rounded-tl-[12px]">
                  Name
                </th>
                <th className="text-left min-w-[250px] py-3 px-5">Email</th>
                <th className="text-left min-w-[100px] py-3 px-5">Role</th>
                <th className="text-left min-w-[100px] py-3 px-5">Status</th>
                <th className="text-left min-w-[150px] py-3 px-5">Joined</th>
                <th className="text-left min-w-[200px] py-3 px-5 rounded-tr-[12px]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1E1E1E] divide-opacity-25">
              {loading && (
                <tr>
                  <td
                    className="py-8 text-center px-5 text-sm text-gray-500"
                    colSpan={6}
                  >
                    Loading users…
                  </td>
                </tr>
              )}
              {!loading && users.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="py-8 text-center px-5 text-sm text-gray-500"
                  >
                    No users found.
                  </td>
                </tr>
              )}
              {!loading &&
                users.map((u) => {
                  const fullName =
                    `${u.firstName || ""} ${u.lastName || ""}`.trim() || "N/A";
                  return (
                    <tr key={u.id} className="my-1">
                      <td className="py-3 px-5 text-[#000025] opacity-75 text-body">
                        {fullName}
                      </td>
                      <td className="py-3 px-5 text-[#000025] opacity-75 text-body">
                        {u.email}
                      </td>
                      <td className="py-3 px-5">
                        <span
                          className={`text-[14px] font-[600] px-2.5 py-1 rounded-full capitalize whitespace-nowrap leading-none ${"bg-[#CBECF9] text-[#036]"}`}
                        >
                          {u.role}
                        </span>
                      </td>
                      <td className="py-3 px-5">
                        <span
                          className={`text-[14px] font-[600] px-2.5 py-1 rounded-full capitalize whitespace-nowrap leading-none ${
                            u.is_blocked
                              ? "bg-[#FFD9D9] text-[#FF6E6E]"
                              : u.is_active
                              ? "bg-[#DCFCE7] text-[#16653F]"
                              : "bg-[#FFD9D9] text-[#FF6E6E]"
                          }`}
                        >
                          {u.is_blocked
                            ? "blocked"
                            : u.is_active
                            ? "active"
                            : "inactive"}
                        </span>
                      </td>
                      <td className="py-3 px-5 text-[#000025] opacity-50 text-body">
                        {u.created_at
                          ? new Date(u.created_at).toLocaleDateString()
                          : "—"}
                      </td>
                      <td className="py-3 px-5">
                        <div className="flex items-center gap-4">
                          <button
                            onClick={() => handleOpenUser(u)}
                            className="opacity-50 border border-[#1E1E1E] rounded-[8px] flex items-center justify-center border-opacity-50 min-h-[42px] min-w-[42px]"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <path
                                d="M2.42012 12.7132C2.28394 12.4975 2.21584 12.3897 2.17772 12.2234C2.14909 12.0985 2.14909 11.9015 2.17772 11.7766C2.21584 11.6103 2.28394 11.5025 2.42012 11.2868C3.54553 9.50484 6.8954 5 12.0004 5C17.1054 5 20.4553 9.50484 21.5807 11.2868C21.7169 11.5025 21.785 11.6103 21.8231 11.7766C21.8517 11.9015 21.8517 12.0985 21.8231 12.2234C21.785 12.3897 21.7169 12.4975 21.5807 12.7132C20.4553 14.4952 17.1054 19 12.0004 19C6.8954 19 3.54553 14.4952 2.42012 12.7132Z"
                                stroke="#1E1E1E"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M12.0004 15C13.6573 15 15.0004 13.6569 15.0004 12C15.0004 10.3431 13.6573 9 12.0004 9C10.3435 9 9.0004 10.3431 9.0004 12C9.0004 13.6569 10.3435 15 12.0004 15Z"
                                stroke="#1E1E1E"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </button>
                          {u.is_blocked ? (
                            <button
                              onClick={() => handleUnblockUser(u.id)}
                              className="min-h-[42px] min-w-[42px] rounded-[8px] bg-[#B9FFCC] flex items-center justify-center hover:bg-[#A0E8B8] transition-colors"
                              title="Unblock user"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                              >
                                <path
                                  d="M20 6 9 17l-5-5"
                                  stroke="#16653F"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button>
                          ) : (
                            <button
                              onClick={() => handleBlockUser(u.id)}
                              className="min-h-[42px] min-w-[42px] rounded-[8px] bg-[#FFD9D9] flex items-center justify-center hover:bg-[#FFC2C2] transition-colors"
                              title="Block user"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                              >
                                <circle
                                  cx="12"
                                  cy="12"
                                  r="9"
                                  stroke="#FF6E6E"
                                  strokeWidth="2"
                                />
                                <path
                                  d="M7.5 7.5 16.5 16.5"
                                  stroke="#FF6E6E"
                                  strokeWidth="2"
                                />
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
      <UserInfoModal
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        user={selectedUser}
        onUserUpdated={fetchUsers}
      />
    </LayoutAdmin>
  );
};

export default AdminUsers;
